// Fire-and-forget client crash reporting. Posts to the backend
// `/monitoring/client-errors` endpoint, which logs a structured line (visible
// in Render logs) and persists it for admin review.
//
// Reporting is OFF in development by default so local crashes don't spam the
// backend; it's on in production, and can be forced on locally with
// REACT_APP_ENABLE_ERROR_REPORTING=true. This module must NEVER throw — an
// error reporter that crashes is worse than no reporter.

import { currentRedactedPath } from "./redactPath";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const isEnabled = () =>
  process.env.NODE_ENV === "production" ||
  process.env.REACT_APP_ENABLE_ERROR_REPORTING === "true";

const readToken = () => {
  try {
    return localStorage.getItem("token") || undefined;
  } catch {
    return undefined;
  }
};

// The server refuses to persist more than this and truncates the rest:
// MAX_STACK = 4000 and MAX_MESSAGE = 2000 in
// server/monitoring/clientErrorController.js, applied to `message`, `stack` and
// `componentStack` on the way into the database. Sending more than that uploads
// bytes the server is going to throw away.
//
// Clamping uses the server's numbers, so nothing is lost that the server would
// have kept: a value clamped here is `max` characters plus a marker, which is
// still longer than `max`, so the server truncates it again — to the first `max`
// characters plus the server's marker, byte for byte what it would have stored
// from the untruncated original. (Pinned by errorReporter.delivery.test.js,
// "stores exactly what the server would have stored from the full value"; that
// equivalence turns on the number, not on the marker.) The marker string is the
// server's too, so a payload read in flight is marked the same way it will be
// marked in the database.
const MAX_STACK = 4000;
const MAX_MESSAGE = 2000;
const TRUNCATION_MARKER = "…[truncated]";

const clamp = (value, max) =>
  typeof value === "string" && value.length > max
    ? `${value.slice(0, max)}${TRUNCATION_MARKER}`
    : value;

// Extracted so the dedupe key in the global listeners is derived from the same
// message the payload is built from. (The payload's copy is clamped afterwards;
// the key uses the unclamped string, which can only make the key finer.)
const messageOf = (error) =>
  (error && (error.message || String(error))) || "Unknown client error";

/**
 * What happened to the reports this page session tried to send.
 *
 * `.catch(() => {})` used to make a 429 from the intake limiter, a 413, a 5xx
 * and an unreachable API byte-for-byte indistinguishable from a delivered
 * report, and `reportClientError` returns undefined, so no caller could branch
 * on the outcome either. This counter is the whole of the delivery signal: it is
 * deliberately not wired to any UI, because a crash screen is the wrong place to
 * start a second conversation about the crash reporter.
 */
const reportingStats = {
  attempted: 0,
  delivered: 0,
  dropped: 0,
  // HTTP status of the most recent drop, or null when no numeric status was
  // available — chiefly a request that never got a response at all, because the
  // API is unreachable or the browser refused to send it.
  lastDropStatus: null,
  deduped: 0,
  overBudget: 0,
};

const ZERO_STATS = { ...reportingStats };

export const getErrorReportingStats = () => ({ ...reportingStats });

const noteDrop = (status) => {
  reportingStats.dropped += 1;
  reportingStats.lastDropStatus = typeof status === "number" ? status : null;
};

export const reportClientError = ({
  error,
  componentStack,
  errorId,
  source = "error_boundary",
} = {}) => {
  if (!isEnabled()) return;
  try {
    const message = messageOf(error);
    const stack = error && error.stack ? String(error.stack) : undefined;

    const payload = {
      errorId,
      message: clamp(message, MAX_MESSAGE),
      stack: clamp(stack, MAX_STACK),
      componentStack: clamp(componentStack, MAX_STACK),
      source,
      // Route shape only. The raw pathname carries a single-use token on the
      // password-reset and email-verification screens, so a crash there used to
      // ship a live token into the crash table.
      path: currentRedactedPath() || undefined,
      appVersion: process.env.REACT_APP_VERSION,
    };

    const token = readToken();
    reportingStats.attempted += 1;
    // keepalive lets the request survive a page unload (crashes often precede
    // a reload). Never let a rejected fetch bubble up.
    fetch(`${apiUrl}/monitoring/client-errors`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "x-auth-token": token } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(
        (res) => {
          if (res && res.ok) reportingStats.delivered += 1;
          else noteDrop(res && res.status);
        },
        // Rejected rather than answered: the API is unreachable, the request was
        // aborted, or the browser refused to send it. Only `res.ok` is read, so
        // nothing here needs a body — reading one would break on a response that
        // has no `.text()`.
        () => noteDrop()
      )
      // Belt and braces: a throw inside either handler above must not surface as
      // an unhandled rejection.
      .catch(() => {});
  } catch {
    // Swallow — reporting must never itself surface an error.
  }
};

// Render a rejection reason as a message string without ever throwing. This
// runs in the `unhandledrejection` listener body, outside reportClientError's
// try/catch, so a throw here crashes the listener and the rejection is never
// reported at all. JSON.stringify throws on circular structures and on BigInt.
// String() is the fallback because it renders a BigInt usefully, but it throws
// on its own for a null-prototype object, so it is guarded too. Only reached
// for reasons that are neither an Error nor a string.
const describeRejectionReason = (reason) => {
  try {
    return JSON.stringify(reason);
  } catch {
    // Fall through to String().
  }
  try {
    return String(reason);
  } catch {
    return "Unserializable rejection reason";
  }
};

/**
 * How many reports the two global listeners may send in one page session.
 *
 * The listeners fire on every `error` and `unhandledrejection` event, so a fault
 * inside an interval or an animation frame produces one POST per tick. The
 * intake route is limited to 30 posts per 15 minutes per IP (clientErrorLimiter
 * in server/middleware/rateLimiter.js, mounted on POST /monitoring/client-errors
 * in server/monitoring/monitoringRoutes.js), and once that budget is spent the
 * server rejects the next report — including a genuinely new crash that arrives
 * after the noisy one.
 *
 * 20 is NOT a share of that 30, and an earlier draft of this comment claimed it
 * was. The two bound different things: the limiter refills every 15 minutes and
 * is keyed per IP across every tab, while this counter is a lifetime total for
 * one page session and never refills. Neither implies the other — a long
 * session sits under 20 forever, and three tabs can still spend the 30 between
 * them. 20 is chosen only as a number big enough to carry the distinct faults a
 * real session produces and small enough that one looping bug cannot spend the
 * shared budget by itself.
 *
 * This bounds a page session, not an IP: a reload starts a new session and a
 * fresh allowance. That is the same reset lifetime ErrorBoundary chose for its
 * ledger, for the same stated reason — a reload is a new incident.
 */
export const MAX_GLOBAL_REPORTS_PER_SESSION = 20;

/**
 * Identity of a fault seen by the global listeners.
 *
 * The shape mirrors ErrorBoundary's errorSignature, which joins the message to a
 * whitespace-trimmed component stack with "::". A global error has no component
 * stack, so the JS stack stands in for it, and `source` is added: the same
 * message arriving as a window error and as an unhandled rejection is two
 * different diagnostic facts, and the two payloads differ.
 *
 * Only the head of the stack is used. The whole stack is too fine — one fault
 * reached through varying async call paths would mint a fresh key every time and
 * the flood would continue.
 *
 * READ THIS BEFORE TRUSTING THE KEY. The head discriminates only when the head
 * frames themselves differ, and for this app's HTTP errors they do not. Measured
 * against the real axios: two ECONNREFUSED failures raised from two different
 * call sites to two different endpoints produce byte-identical first three
 * frames, because all three sit inside axios (AxiosError.from,
 * RedirectableRequest.handleRequestError) and the app's own frame is across an
 * async boundary and never appears at all. Those two DO collide and the second
 * is deduped away. So this key does not deliver a general "two same-message
 * crashes stay apart" property — it delivers it only where the throw site is in
 * app code. Both directions are pinned in errorReporter.dedupe.test.js.
 *
 * WHAT THIS DELIBERATELY COLLAPSES: a report with no stack keys on source and
 * message alone. That covers a cross-origin script error, where `event.error` is
 * null and only `event.message` survives, and a rejection reason that is an
 * object JSON.stringify cannot serialise, which renders as "[object Object]".
 * In those cases a second distinct crash sharing the degraded message is
 * suppressed. That is the price of the ceiling below applying to them too, and
 * it is the better trade: both cases are already near-useless to debug from, and
 * both are exactly the shapes that arrive in floods.
 */
const SIGNATURE_STACK_LINES = 3;

const globalErrorSignature = (source, message, stack) =>
  `${source}::${message}::${String(stack || "")
    .trim()
    .split("\n")
    .slice(0, SIGNATURE_STACK_LINES)
    .map((line) => line.trim())
    .join("|")}`;

// Signatures already filed this page session. Never cleared on a timer: see
// MAX_GLOBAL_REPORTS_PER_SESSION on why a reload is the reset.
const seenGlobalSignatures = new Set();
let globalReportsSent = 0;

/**
 * One report per distinct fault per page session, capped in total.
 *
 * Same discipline as ErrorBoundary's `if (alreadySeen) return;` — a repeat is
 * not new information — but the ceiling is here as well, because a seen-set
 * alone does not bound anything: a message that embeds a varying id or URL mints
 * a fresh signature every occurrence.
 *
 * The signature is recorded only when a report is actually sent, so the set can
 * never hold more than MAX_GLOBAL_REPORTS_PER_SESSION entries and needs no size
 * cap of its own.
 */
const reportGlobalError = ({ error, source }) => {
  try {
    // Checked here as well as in reportClientError so a session with reporting
    // switched off does not consume the ledger or the budget.
    if (!isEnabled()) return;

    const signature = globalErrorSignature(
      source,
      messageOf(error),
      error && error.stack
    );

    if (seenGlobalSignatures.has(signature)) {
      reportingStats.deduped += 1;
      return;
    }
    if (globalReportsSent >= MAX_GLOBAL_REPORTS_PER_SESSION) {
      reportingStats.overBudget += 1;
      return;
    }

    seenGlobalSignatures.add(signature);
    globalReportsSent += 1;
    reportClientError({ error, source });
  } catch {
    // This runs in a listener body, outside reportClientError's try/catch.
    // messageOf calls String(error), which throws for a null-prototype object.
  }
};

/**
 * Test-only. Clears the per-session ledger, the budget and the counters, so one
 * suite can dispatch the same fault in two tests without the second being
 * deduped away. This is what a page reload does.
 *
 * Deliberately does not clear `installed`: the handler suites install once in
 * beforeAll, and re-installing would register a second copy of both listeners.
 */
export const __resetErrorReportingForTests = () => {
  seenGlobalSignatures.clear();
  globalReportsSent = 0;
  Object.assign(reportingStats, ZERO_STATS);
};

let installed = false;

/**
 * Catch errors that never reach a React ErrorBoundary: async throws, event
 * handlers, and unhandled promise rejections. Install once at app startup.
 */
export const installGlobalErrorHandlers = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    reportGlobalError({
      error: event.error || { message: event.message },
      source: "window_error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    try {
      const reason = event.reason;
      reportGlobalError({
        error:
          reason instanceof Error
            ? reason
            : {
                message:
                  typeof reason === "string"
                    ? reason
                    : describeRejectionReason(reason),
              },
        source: "unhandled_rejection",
      });
    } catch {
      // `reason instanceof Error` reads the reason's prototype and so runs a
      // Proxy's getPrototypeOf trap, which can throw. Nothing here is inside
      // reportClientError's try/catch, and a throw out of a listener is the one
      // outcome the module docblock rules out.
    }
  });
};
